'use strict';

/**
 * Utils (refactor v2 - drop-in)
 * Goals:
 * - Keep existing public API names to avoid breaking other scripts.
 * - Reduce redundant /cart.js calls (micro-cache + in-flight de-dupe).
 * - Consolidate cookie helpers.
 * - Keep observeSelector/waitForElement behavior, reduce overhead.
 * - Reduce console overhead by gating logs behind WBLS_UTILS_DEBUG.
 */

/* ---------------------------------------------
 * Config / Debug
 * ------------------------------------------- */
const WBLS_UTILS_DEBUG = false;
const log = (...args) => WBLS_UTILS_DEBUG && console.log('[WBLS utils]', ...args);
const warn = (...args) => WBLS_UTILS_DEBUG && console.warn('[WBLS utils]', ...args);
const err = (...args) => console.error('[WBLS utils]', ...args);

/* ---------------------------------------------
 * Variation (kept for backwards compatibility)
 * ------------------------------------------- */
let variation;

/* ---------------------------------------------
 * Cookies (single implementation)
 * ------------------------------------------- */
const Cookie = {
  get(name) {
    try {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(';').shift() || null;
      return null;
    } catch (e) {
      err('Error reading cookie:', name, e?.message || e);
      return null;
    }
  },

  set(name, value, days, opts = {}) {
    try {
      const d = new Date();
      d.setTime(d.getTime() + (days * 24 * 60 * 60 * 1000));
      const expires = `expires=${d.toUTCString()}`;
      const path = opts.path || '/';
      const domain = opts.domain ? `;domain=${opts.domain}` : '';
      const secure = opts.secure ? ';secure' : '';
      const sameSite = opts.sameSite ? `;samesite=${opts.sameSite}` : '';
      document.cookie = `${name}=${value};${expires};path=${path}${domain}${secure}${sameSite}`;
    } catch (e) {
      err('Error setting cookie:', name, e?.message || e);
    }
  },

  del(name, path, domain) {
    try {
      if (!Cookie.get(name)) return;
      document.cookie =
        `${name}=;` +
        (path ? `path=${path};` : '') +
        (domain ? `domain=${domain};` : '') +
        `expires=Thu, 01 Jan 1970 00:00:01 GMT`;
    } catch (e) {
      err('Error deleting cookie:', name, e?.message || e);
    }
  }
};

// GTM wrappers (keep names + behavior)
function gtm_getCookie(name) {
  const v = Cookie.get(name);
  return v === null ? null : v;
}

function gtm_setCookie(name, value, days) {
  // keep domain placeholder behavior
  Cookie.set(name, value, days, { domain: '.{{*Base URL}}', path: '/' });
}

/* legacy cookie helpers kept */
const getCookie = (name) => Cookie.get(name);
const setCookie = (name, value, expdays) => Cookie.set(name, value, expdays, { path: '/' });
const delCookie = (name, path, domain) => Cookie.del(name, path, domain);

/* ---------------------------------------------
 * URL params
 * ------------------------------------------- */
const getParam = (name, optSearch) => {
  optSearch = optSearch || location.search;
  name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
  const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
  const results = regex.exec(optSearch);
  return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
};

/* ---------------------------------------------
 * Experiments
 * ------------------------------------------- */
const initExperiment = (exp_id, iteration = 'v0.01', force_activate_variation) => {
  const exp_handle = exp_id.replace('-', '').toLowerCase();

  if (force_activate_variation) {
    variation = force_activate_variation;
  } else {
    const qa = getParam('qa');
    if (qa && qa.indexOf(exp_handle) > -1) {
      variation = qa.replace(exp_handle, '');
    } else {
      variation = window.sessionStorage.getItem(exp_id);
    }
  }

  const exp_tag = exp_handle + variation;
  document.documentElement.classList.add(exp_tag);
  log(`init ${exp_id}${variation} -- ${iteration}`);
  return variation;
};

/* ---------------------------------------------
 * Timing helpers
 * ------------------------------------------- */
// requestAnimationFrame shim (original had ~50ms fallback; use ~16.7ms)
const requestAnimFrame = (function () {
  return (
    window.requestAnimationFrame ||
    window.webkitRequestAnimationFrame ||
    window.mozRequestAnimationFrame ||
    function (callback) {
      window.setTimeout(callback, 1000 / 60);
    }
  );
})();

function waitUntil(test) {
  return new Promise(function (resolve) {
    (function poll() {
      if (!test()) return requestAnimFrame(poll);
      return resolve();
    })();
  });
}

/* ---------------------------------------------
 * DOM observers
 * ------------------------------------------- */
function waitForElement(selector) {
  return new Promise(function (resolve) {
    const el = document.querySelector(selector);
    if (el) return resolve(el);

    const observer = new MutationObserver(function (mutations) {
      for (let i = 0; i < mutations.length; i++) {
        const nodes = mutations[i].addedNodes;
        if (!nodes || !nodes.length) continue;

        for (let j = 0; j < nodes.length; j++) {
          const node = nodes[j];

          if (node && node.matches && node.matches(selector)) {
            observer.disconnect();
            return resolve(node);
          }

          // If a subtree node got added, check within it (cheap fast path)
          if (node && node.querySelector) {
            const found = node.querySelector(selector);
            if (found) {
              observer.disconnect();
              return resolve(found);
            }
          }
        }
      }
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });
  });
}

const observeSelector = (
  selector,
  callback,
  options = { timeout: null, once: false, onTimeout: null, document: window.document }
) => {
  // Use WeakSet to avoid leaks; elements can be GC'd
  let processed = new WeakSet();

  if (options.timeout || options.onTimeout) {
    warn('observeSelector: timeout/onTimeout options exist but are not implemented (legacy behavior).');
  }

  let obs;
  let isDone = false;

  const done = () => {
    if (obs) obs.disconnect();
    processed = null;
    obs = null;
    isDone = true;
    return true;
  };

  const processElement = (el) => {
    if (!processed || processed.has(el)) return false;
    processed.add(el);
    callback(el);
    if (options.once) return done();
    return false;
  };

  const lookForSelector = (root = document) => {
    if (!root) return false;

    if (root.matches && root.matches(selector)) {
      if (processElement(root)) return true;
    }

    if (root.querySelectorAll) {
      const els = root.querySelectorAll(selector);
      for (let i = 0; i < els.length; i++) {
        if (processElement(els[i])) return true;
      }
    }
    return false;
  };

  // initial scan
  lookForSelector();

  if (!isDone) {
    obs = new MutationObserver((records) => {
      for (let r = 0; r < records.length; r++) {
        const record = records[r];
        const added = record.addedNodes;
        if (!added || !added.length) continue;

        for (let i = 0; i < added.length; i++) {
          const node = added[i];
          // check node itself and its subtree without always jumping to parentElement
          if (node && lookForSelector(node)) return;
          if (node && node.parentElement && lookForSelector(node.parentElement)) return;
        }
      }
    });

    obs.observe(options.document || document, {
      attributes: false,
      childList: true,
      subtree: true
    });
  }

  return () => done();
};

/* ---------------------------------------------
 * Cart API (de-dupe /cart.js calls)
 * ------------------------------------------- */
const CartAPI = (() => {
  let inflight = null;
  let cache = null;
  let cacheAt = 0;

  // Small cache window to prevent bursty duplication (tune as needed)
  const CACHE_MS = 400;

  const fetchCartJson = () => {
    const now = Date.now();

    if (cache && now - cacheAt < CACHE_MS) {
      return Promise.resolve(cache);
    }

    if (inflight) return inflight;

    // Prefer jQuery ajax if available to preserve existing behavior shape
    if (window.$ && $.ajax) {
      inflight = $.ajax({
        type: 'GET',
        url: '/cart.js',
        dataType: 'json'
      })
        .then((data) => {
          cache = data;
          cacheAt = Date.now();
          return data;
        })
        .finally(() => {
          inflight = null;
        });

      return inflight;
    }

    inflight = fetch('/cart.js', { credentials: 'same-origin' })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch /cart.js');
        return res.json();
      })
      .then((data) => {
        cache = data;
        cacheAt = Date.now();
        return data;
      })
      .finally(() => {
        inflight = null;
      });

    return inflight;
  };

  const clearCache = () => {
    cache = null;
    cacheAt = 0;
  };

  // Alias for readability after mutations
  const bump = () => clearCache();

  return { fetchCartJson, clearCache, bump };
})();

/* ---------------------------------------------
 * Cart attribute updates (kept)
 * ------------------------------------------- */
function gtm_add_cartAttribute(attr_name, attr_val, multiple_attr_array = []) {
  let body_attributes = `attributes[${attr_name}]=${attr_val}`;
  if (multiple_attr_array.length > 0) {
    body_attributes = multiple_attr_array.join('&');
  }

  fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
    body: body_attributes
  })
    .then((response) => {
      if (!response.ok) throw new Error('Network response was not ok.');
      return response.json();
    })
    .then((data) => {
      log('Cart updated successfully:', data);
      window.cart_attributes_updated = true;
      CartAPI.bump();
    })
    .catch((error) => {
      err('Failed to update cart:', error);
    });
}

/* add experiment data to cart (kept; slightly safer build) */
setTimeout(() => {
  const exp_cart_data = [];
  const dl = window.dataLayer;

  if (dl && Array.isArray(dl)) {
    for (let i = 0; i < dl.length; i++) {
      const item = dl[i];
      if (item && typeof item === 'object' && item.event === 'proteus_experiment_pageview') {
        const exp_id = item.proteus_exp_id;
        const exp_var = item.proteus_var_id;
        exp_cart_data.push(
          `attributes[proteus-experimentation-${String(exp_id).toLowerCase()}]=[${exp_id}]${exp_var}`
        );
      }
    }
  }

  if (exp_cart_data.length > 0) {
    log('add cart attributes exp_cart_data', exp_cart_data);
    gtm_add_cartAttribute(false, false, exp_cart_data);
  }
}, 7500);

/* ---------------------------------------------
 * Script loader (kept)
 * ------------------------------------------- */
const js_loadScript = (url, optCallback) => {
  const s = document.createElement('script');
  s.type = 'text/javascript';
  s.async = true;
  s.src = url;
  if (typeof optCallback === 'function') s.onload = optCallback;
  const first = document.getElementsByTagName('script')[0];
  first.parentNode.insertBefore(s, first);
};

/* ---------------------------------------------
 * Cart helpers (kept)
 * ------------------------------------------- */
const get_cart_json = () => CartAPI.fetchCartJson();

/**
 * Choose special properties based on session flags (keeps legacy behavior)
 */
const getSpecialAtcProperties = () => {
  if (window.sessionStorage.getItem('WBLS-Progressive-Cart-Mystery')) return { Gift: 'Mystery' };
  if (window.sessionStorage.getItem('WBLS-37')) return { QA: 'WBLS-37' };
  if (window.sessionStorage.getItem('WBLS-31')) return { Email: 'Free Gift' };
  return null;
};

async function add_items_to_cart(items) {
  log('add_items_to_cart()', items);

  return fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    },
    body: JSON.stringify(items)
  })
    .then((response) => response.json())
    .then((data) => {
      log('Item added to cart', data);
      CartAPI.bump();
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      return data;
    })
    .catch((error) => {
      err('Error adding item to cart:', error);
      throw error;
    });
}

const cart_remove_items = (var_ids, callback) => {
  log(`cart_remove_items(${var_ids})`);

  const removeSet = new Set(var_ids);

  get_cart_json().then((data) => {
    for (let i = 0; i < data.items.length; i++) {
      const id = data.items[i].variant_id;
      if (!removeSet.has(id)) continue;

      log(`... removing ${id}`);

      if (window.$ && $.ajax) {
        $.ajax({
          type: 'POST',
          url: '/cart/change.js',
          data: { quantity: 0, id },
          dataType: 'json',
          success: function (resp) {
            CartAPI.bump();
            if (typeof callback === 'function') callback(resp);
          }
        });
      } else {
        fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
          body: `quantity=0&id=${encodeURIComponent(id)}`
        })
          .then((r) => r.json())
          .then((resp) => {
            CartAPI.bump();
            if (typeof callback === 'function') callback(resp);
          })
          .catch((e) => err('cart_remove_items change failed', e));
      }
    }
  });
};

const cart_add_item = (var_id, qty, callback) => {
  log(`cart_add_item(${var_id}, ${qty})`);

  // Preserve existing experiment/mystery properties behavior
  const atc_data = { quantity: qty, id: var_id };
  const props = getSpecialAtcProperties();
  if (props) atc_data.properties = props;

  if (window.$ && $.ajax) {
    $.ajax({
      type: 'POST',
      url: '/cart/add.js',
      data: atc_data,
      dataType: 'json',
      success: function (resp) {
        CartAPI.bump();
        if (typeof callback === 'function') callback(resp);
      }
    });
    return;
  }

  // fetch fallback if jQuery is missing
  const body = new URLSearchParams();
  Object.keys(atc_data).forEach((k) => {
    if (k === 'properties' && atc_data.properties) {
      Object.keys(atc_data.properties).forEach((pk) => {
        body.append(`properties[${pk}]`, String(atc_data.properties[pk]));
      });
    } else {
      body.append(k, String(atc_data[k]));
    }
  });

  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
    body: body.toString()
  })
    .then((r) => r.json())
    .then((resp) => {
      CartAPI.bump();
      if (typeof callback === 'function') callback(resp);
    })
    .catch((e) => err('cart_add_item failed', e));
};

const cart_contains_subscription = () => {
  return get_cart_json().then((data) => {
    for (let i = 0; i < data.items.length; i++) {
      const props = data.items[i]?.properties;
      if (props && props.shipping_interval_frequency) return true;
    }
    return false;
  });
};

const cart_contains_item = (var_id) => {
  return get_cart_json().then((data) => {
    for (let i = 0; i < data.items.length; i++) {
      if (data.items[i].variant_id === var_id) return true;
    }
    return false;
  });
};

/* ---------------------------------------------
 * GA configured event (kept)
 * ------------------------------------------- */
waitUntil(() => typeof gtag !== 'undefined')
  .then(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'ga_configured' });
  })
  .catch(() => {});

/* ---------------------------------------------
 * Disable ATC until cart is "ready"
 * - lazy: only runs when ATC exists
 * - uses get_cart_json() so /cart.js fetch is de-duped with other callers
 * ------------------------------------------- */
waitForElement('body')
  .then((doc) => {
    if (doc.getAttribute('data-atc-active') === 'true') return;

    const enableAtc = () => doc.setAttribute('data-atc-active', 'true');
    const disableAtc = () => doc.setAttribute('data-atc-active', 'false');

    const ATC_SELECTOR =
      '[data-add-to-cart], form[action^="/cart/add"], button[name="add"], [class*="add-to-cart"]';

    const runCartReadyCheckOnce = (() => {
      let ran = false;
      return () => {
        if (ran) return;
        ran = true;

        disableAtc();

        // De-duped cart readiness check
        get_cart_json()
          .then(() => {
            setTimeout(enableAtc, 1250);
          })
          .catch(() => {
            // preserve legacy silent catch
          });

        // hard fallback: enable after 5s no matter what
        setTimeout(enableAtc, 5000);
      };
    })();

    if (doc.querySelector(ATC_SELECTOR)) {
      runCartReadyCheckOnce();
      return;
    }

    observeSelector(
      ATC_SELECTOR,
      () => {
        runCartReadyCheckOnce();
      },
      { once: true, document: doc }
    );
  })
  .catch(() => {});